import React, { useState } from 'react';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  CheckCircle, 
  Send, 
  ArrowRight,
  MessageCircle,
  Calendar,
  Users,
  Truck,
  Building2
} from 'lucide-react';
import AnimatedBackground from '../components/AnimatedBackground';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    fleetSize: '',
    productInterest: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setIsSubmitting(true);

    const accessKey = import.meta.env.VITE_WEB3FORMS_ACCESS_KEY;

    if (!accessKey) {
      setSubmitError('Email service is not configured yet. Please try again later or contact info@gaadimatic.com directly.');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_key: accessKey,
          subject: 'New Demo Request - GaadiMatic Website',
          from_name: `${formData.firstName} ${formData.lastName}`.trim(),
          to_email: 'info@gaadimatic.com',
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          fleet_size: formData.fleetSize,
          product_interest: formData.productInterest || 'Not specified',
          message: formData.message || 'No additional message provided',
          botcheck: '',
        }),
      });

      const result = (await response.json()) as { success?: boolean; message?: string };

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Submission failed');
      }

      setIsSubmitted(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        fleetSize: '',
        productInterest: '',
        message: '',
      });
    } catch (error) {
      console.error('Failed to submit contact form:', error);
      setSubmitError('Unable to send your request right now. Please call us at +91 99165 00454 or email info@gaadimatic.com.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      details: ['+91 99165 00454'],
      action: 'tel:+919916500454',
      actionText: 'Call now',
      color: 'bg-green-50 text-green-600'
    },
    {
      icon: Mail,
      title: 'Email',
      details: ['info@gaadimatic.com'],
      action: 'mailto:info@gaadimatic.com',
      actionText: 'Send email',
      color: 'bg-blue-50 text-blue-600'
    },
    {
      icon: MapPin,
      title: 'Office',
      details: ['Hyderabad, India'],
      action: null,
      actionText: null,
      color: 'bg-orange-50 text-orange-600'
    },
    {
      icon: Clock,
      title: 'Business Hours',
      details: ['Mon - Sat: 9:00 AM - 6:00 PM IST'],
      action: null,
      actionText: null,
      color: 'bg-purple-50 text-purple-600'
    }
  ];

  const faqs = [
    {
      question: 'How quickly can I get started?',
      answer: 'Most fleets are up and running within 24-48 hours. Our onboarding team will guide you through the setup process, including data migration and team training.'
    },
    {
      question: 'Do you offer a free trial?',
      answer: 'Yes! We offer a 14-day free trial with full access to all features. No credit card required to start.'
    },
    {
      question: 'What fleet sizes do you support?',
      answer: 'GaadiMatic FleetX is designed to scale with you. We support fleets ranging from 10 vehicles to 10,000+ vehicles across multiple locations.'
    },
    {
      question: 'Do you provide training and support?',
      answer: 'Absolutely. Every customer gets personalized onboarding training, access to our help center, and ongoing support via phone, email, and chat.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <AnimatedBackground />
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden bg-gray-50">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-50/50 via-transparent to-accent-orange/5" />
        <div className="container-main relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-100 rounded-full mb-6">
              <MessageCircle className="w-4 h-4 text-primary-600" />
              <span className="text-sm font-medium text-primary-700">We're here to help</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-navy-950 leading-tight mb-6">
              Let's Start a <span className="text-primary-600">Conversation</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
              Ready to transform your fleet operations? Get in touch with our team for a personalized demo 
              tailored to your specific needs.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-white -mt-10">
        <div className="container-main">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((item, index) => (
              <div key={index} className="card p-6 text-center">
                <div className={`w-14 h-14 ${item.color} rounded-xl flex items-center justify-center mx-auto mb-4`}>
                  <item.icon className="w-7 h-7" />
                </div>
                <h3 className="text-lg font-semibold text-navy-950 mb-2">{item.title}</h3>
                {item.details.map((detail, i) => (
                  <p key={i} className="text-gray-600 text-sm">{detail}</p>
                ))}
                {item.action && (
                  <a 
                    href={item.action}
                    className="inline-flex items-center gap-1 text-primary-600 font-medium text-sm mt-3 hover:underline"
                  >
                    {item.actionText}
                    <ArrowRight className="w-4 h-4" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Demo Request Form Section */}
      <section className="section-padding bg-gray-50" id="demo">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Left Column - Info */}
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-6">
                Request a Personalized Demo
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                See how GaadiMatic FleetX can transform your fleet operations. Fill out the form 
                and our team will get back to you within 24 hours to schedule your demo.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-navy-950 mb-1">30-Minute Demo</h3>
                    <p className="text-gray-600 text-sm">Live walkthrough of features relevant to your fleet</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-navy-950 mb-1">Free Consultation</h3>
                    <p className="text-gray-600 text-sm">Get expert advice on optimizing your fleet operations</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Truck className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-navy-950 mb-1">Custom ROI Analysis</h3>
                    <p className="text-gray-600 text-sm">See potential cost savings for your specific fleet</p>
                  </div>
                </div>
              </div>

              <div className="mt-10 p-6 bg-white rounded-xl border border-gray-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex -space-x-2">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full border-2 border-white flex items-center justify-center">
                        <Users className="w-4 h-4 text-white" />
                      </div>
                    ))}
                  </div>
                  <span className="text-sm text-gray-600">500+ fleet operators trust GaadiMatic</span>
                </div>
                <p className="text-sm text-gray-500 italic">
                  "The demo was incredibly helpful. Within 30 minutes, we could see exactly how 
                  GaadiMatic would solve our specific challenges." — Rajesh K., Fleet Manager
                </p>
              </div>
            </div>

            {/* Right Column - Form */}
            <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-10">
              {!isSubmitted ? (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        required
                        value={formData.firstName}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                        placeholder="John"
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        required
                        value={formData.lastName}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                        placeholder="Doe"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Business Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                      placeholder="john@company.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                      placeholder="+91-9876543210"
                    />
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                      Company Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      required
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                      placeholder="Your Company Ltd."
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="fleetSize" className="block text-sm font-medium text-gray-700 mb-2">
                        Fleet Size <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="fleetSize"
                        name="fleetSize"
                        required
                        value={formData.fleetSize}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                      >
                        <option value="">Select range</option>
                        <option value="10-50">10-50 vehicles</option>
                        <option value="50-200">50-200 vehicles</option>
                        <option value="200-500">200-500 vehicles</option>
                        <option value="500+">500+ vehicles</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="productInterest" className="block text-sm font-medium text-gray-700 mb-2">
                        Product Interest
                      </label>
                      <select
                        id="productInterest"
                        name="productInterest"
                        value={formData.productInterest}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                      >
                        <option value="">Select product</option>
                        <option value="employee-transport">Employee Transport</option>
                        <option value="trucks">Trucks (FTL/PTL)</option>
                        <option value="scmt">SCMT - School Transport</option>
                        <option value="platform">Full Platform</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Additional Message (Optional)
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all resize-none"
                      placeholder="Tell us about your specific needs or challenges..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <Send className="w-5 h-5" />
                        Request Demo
                      </span>
                    )}
                  </button>

                  {submitError && (
                    <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                      {submitError}
                    </div>
                  )}

                  <p className="text-xs text-gray-500 text-center">
                    By submitting this form, you agree to our Privacy Policy. 
                    We'll never share your information with third parties.
                  </p>
                </form>
              ) : (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-navy-950 mb-4">Thank You!</h3>
                  <p className="text-gray-600 mb-6">
                    Your demo request has been received. Our team will contact you within 24 hours 
                    to schedule your personalized demo.
                  </p>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">
                      In the meantime, check your email for a confirmation and some helpful 
                      resources to get you started.
                    </p>
                  </div>
                  <button 
                    onClick={() => setIsSubmitted(false)}
                    className="mt-6 text-primary-600 font-medium hover:underline"
                  >
                    Submit another request
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Have questions? We have answers. Can't find what you're looking for? 
              Reach out to our team directly.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {faqs.map((faq, index) => (
              <div key={index} className="card p-6">
                <h3 className="font-semibold text-navy-950 mb-3 flex items-start gap-3">
                  <span className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 text-primary-600 text-sm font-bold">
                    {index + 1}
                  </span>
                  {faq.question}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed pl-9">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-4">
              Visit Our Office
            </h2>
            <p className="text-lg text-gray-600">
              We're based in Hyderabad, the heart of India's tech corridor.
            </p>
          </div>
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="aspect-[21/9] bg-gradient-to-br from-primary-50 to-gray-100 flex items-center justify-center">
              <div className="text-center">
                <Building2 className="w-16 h-16 text-primary-300 mx-auto mb-4" />
                <p className="text-gray-500">Interactive Map</p>
                <p className="text-sm text-gray-400">Hyderabad, Telangana, India</p>
              </div>
            </div>
            <div className="p-8">
              <div className="grid sm:grid-cols-3 gap-6 text-center">
                <div>
                  <h4 className="font-semibold text-navy-950 mb-2">Address</h4>
                  <p className="text-gray-600 text-sm">
                    GaadiMatic Technologies Pvt. Ltd.<br />
                    Hyderabad, India
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-navy-950 mb-2">Phone</h4>
                  <p className="text-gray-600 text-sm">
                    <a href="tel:+919916500454" className="hover:text-primary-600 transition-colors">
                      +91 99165 00454
                    </a>
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-navy-950 mb-2">Email</h4>
                  <p className="text-gray-600 text-sm">
                    <a href="mailto:info@gaadimatic.com" className="hover:text-primary-600 transition-colors">
                      info@gaadimatic.com
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary-600">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Ready to Transform Your Fleet?
            </h2>
            <p className="text-lg text-primary-100 mb-8">
              Join 500+ fleet operators who have reduced costs by 15-20% with GaadiMatic FleetX.
              Request your personalized demo today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="#demo" 
                className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-primary-600 bg-white rounded-lg transition-all duration-200 hover:bg-gray-100"
              >
                Request Demo
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
              <a 
                href="tel:+919916500454" 
                className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white border-2 border-white/30 rounded-lg transition-all duration-200 hover:bg-white/10"
              >
                <Phone className="mr-2 w-5 h-5" />
                Call Us Now
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
